<!--
This layout will update form control to have the same animation on
form labels based on material-ui design.

Applying CSS class `animated-input-label-layout__input--transformed`
will do the actual transformation.
- `transitions` are set to corresponding elements by default
-->

<template>
  <div class="animated-input-label-layout">
    <div
      ref="inputContainerRef"
      :class="[
        'animated-input-label-layout__input',
        value && 'animated-input-label-layout__input--has-value',
        (isFocused || hasPlaceholder) &&
          'animated-input-label-layout__input--focused',
        (applyTransformation || inputHasPrefix) &&
          'animated-input-label-layout__input--transformed',
      ]"
    >
      <slot />

      <label class="animated-input-label-layout__label">
        {{ label }}
      </label>
    </div>
  </div>
</template>

<style lang="scss">
/**
Structure:
- animated-input-label-layout
  - __input
    - child input element [slot]
    - __label
*/
.animated-input-label-layout {
  &__label {
    position: absolute;
    left: $input-padding-x;

    // Centers label
    top: $input-padding-y;

    font-size: $input-font-size;
    color: $input-label-color;

    transition: all 300ms ease;
    transform-origin: top left;

    pointer-events: none; // prevents blocking of the label
  }

  // Transitions
  &__input {
    position: relative;

    > .ant-input-affix-wrapper,
    > .ant-input,
    .ant-calendar-picker-input.ant-input
      .ant-select:not(.ant-select-customize-input) {
      transition: all 300ms ease;
    }
  }

  // Actual class to do transformation. Only do transformation when label is also provided.
  .animated-input-label-layout__input--transformed {
    > .animated-input-label-layout__label {
      transform: translateY(-50%);
      font-size: $animated-input-label-transformed-font-size;
    }

    > .ant-input-affix-wrapper,
    > .ant-input,
    .ant-calendar-picker-input.ant-input {
      padding-top: $input-padding-y + $animated-input-transform-offset-px;
      padding-bottom: $input-padding-y - $animated-input-transform-offset-px;
    }

    > .ant-select:not(.ant-select-customize-input) {
      padding-top: $input-select-padding-y + $animated-input-transform-offset-px;
      padding-bottom: $input-select-padding-y -
        $animated-input-transform-offset-px;
    }
  }
}
</style>

<script lang="ts">
import { defineComponent, computed, ref, nextTick, onMounted } from "vue";

export default defineComponent({
  props: {
    label: { type: String, required: true },
    value: { required: true },
    isFocused: Boolean,
    hasPlaceholder: Boolean,
  },
  setup(props) {
    const inputContainerRef = ref<HTMLElement | null>(null);
    const inputHasPrefix = ref<boolean>(false);

    const applyTransformation = computed(
      () =>
        props.label && (props.value || props.hasPlaceholder || props.isFocused)
    );

    onMounted(() => {
      nextTick(() => {
        inputHasPrefix.value =
          !!inputContainerRef.value?.querySelector(".ant-input-prefix");
      });
    });

    return { applyTransformation, inputContainerRef, inputHasPrefix };
  },
});
</script>
